import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {DocumentProps, PDFViewer} from '@react-pdf/renderer';
import InsightReport from './InsightReport';

import {assessmentApi} from '../../api/openapi-axios-client';
import { callApi } from '../../api/helpers';
import { UserShareReportData } from '../../types';

const ViewInsightReport: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [previewReport, setPreviewReport] = useState<React.ReactElement | null>(null);

    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const userId = searchParams.get('userId');
    const shareId = searchParams.get('shareId');

    useEffect(() => {
        const getData = async (): Promise<void> => {
            if(isLoading || isError) {
                return;
            }

            // Data was feed in from the state
            if(localStorage.getItem('reportData')) {
                const reportData = JSON.parse(localStorage.getItem('reportData') || '{}');
                localStorage.removeItem('reportData');
                setPreviewReport(<InsightReport reportData={reportData}/>);
                setIsLoading(false);
            }
            else {
                if(!userId || !shareId) {
                    return;
                }

                if(previewReport) {
                    return;
                }

                try {
                    setIsLoading(true);
                    const reportData = await callApi<UserShareReportData>(() => assessmentApi.resultSharing.getShareReportData(userId, shareId));

                    // Set Report
                    setIsLoading(false);
                    setPreviewReport(<InsightReport reportData={reportData}/>);
                }
                catch(e) {
                    setIsLoading(false);
                    setIsError(true);
                }
            }
        };

        getData();
    });

    const buildLoadingControl = (): React.ReactElement => {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height:'100%',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? 2 : 4
            }}>
                <CircularProgress sx={{width: isMobile ? 50 : 75, height: isMobile ? 50 : 75}}/>
                <Typography sx={{
                    fontWeight: 900,
                    fontSize: isMobile ? '18px' : '24px',
                    marginBottom: '20px',
                    flexGrow:1,
                    textAlign: 'center'
                }}>
                    Building Report ...
                </Typography>
            </Box>
        );
    }

    const buildPreviewControl = (): React.ReactElement => {
        if(!previewReport) {
            return (
                <Typography sx={{
                    fontWeight: 900,
                    fontSize: isMobile ? '18px' : '24px',
                    marginBottom: '20px',
                    flexGrow:1,
                    textAlign: 'center',
                    padding: isMobile ? 2 : 0
                }}>
                    Error Loading Report
                </Typography>
            );
        }
        else {
            return (
                <PDFViewer
                    width='100%'
                    height='100%'
                    style={{
                        flexGrow: 1,
                        minHeight: isMobile ? '300px' : '500px'
                    }}
                >
                    {previewReport as React.ReactElement<DocumentProps>}
                </PDFViewer>
            )
        }
    }

    return (
        <Container
            maxWidth={isMobile ? false : "lg"}
            disableGutters={isMobile}
            sx={{
                px: { xs: 2, sm: 3, md: 3 },
                py: { xs: 2, sm: 3, md: 4 },
                width: '100%'
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignContent: 'stretch',
                justifyContent: 'stretch',
                width: '100%',
                height: isMobile ? '80vh' : '90vh',
                marginTop: isMobile ? 0.5 : 1,
                padding: isMobile ? 1 : 2
            }}>
                {isLoading ? buildLoadingControl() : buildPreviewControl()}
            </Box>
        </Container>
    );
}

export default ViewInsightReport; 